import { Injectable } from '@angular/core';
import { defer, forkJoin, Observable, throwError, timer } from 'rxjs';
import { catchError, map, mergeMap, retryWhen } from 'rxjs/operators';
import { CapacitorHttp, HttpResponse } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { StorageService } from './storage.service';
import * as Constants from "../utils/Constants";

export interface Glass {
    id: string;
    glazingCount: number;
    ug: number;
    interiorGlassThickness: number;
    exteriorGlassThickness: number;
    middleGlassThickness?: number | null;
    cavities: number[];
    name: string;
    type: string;
    thickness: number;
    category: string;
    uValue: number;
    gValue: number;
    lightTransmittance: number;
    coating: string;
    manufacturer: number;
    isLowE: boolean;
    region: number[];
    structure: string;
}
/* ===================== TYPES ===================== */

export interface MasterData {
    frameManufacturers: any[];  
    // frames: any[];
    glasses: Glass[];
    // manufacturers: any[];
    spacers: any[];
}

/* ===================== SERVICE ===================== */

@Injectable({ providedIn: 'root' })
export class MasterdataApiService {
    private readonly baseUrl = environment.caluwin_api.base_url;

    private readonly endpoints = {
        // frameManufacturers: environment.caluwin_api.endpoints.md_frames_manufacturers,
        // frames: environment.caluwin_api.endpoints.md_frames,
        glasses: environment.caluwin_api.endpoints.md_glasses,
        frameManufacturers: environment.caluwin_api.endpoints.md_manufacturer,
        spacers: environment.caluwin_api.endpoints.md_spacer,
    };


    private readonly RETRY_COUNT = 2;
    private readonly BASE_DELAY_MS = 200;

    constructor(private storageService: StorageService) { }

    /* ===================== PUBLIC API ===================== */

    /**
     * Fetches master data from API and stores it.
     * If API fails, falls back to stored data (if available).
     */
    loadMasterData(): Observable<MasterData> {
        return this.fetchFromApi().pipe(
            map(data => {
                this.storageService.set(Constants.STORAGE_KEY, data);
                return data;
            }),
            catchError(() => this.loadFromStorage())
        );
    }

    /**
     * Use this when you ONLY want cached data
     */
    async getCachedMasterData(): Promise<MasterData | null> {
        const data = await this.storageService.get(Constants.STORAGE_KEY);
        return data ? JSON.parse(data) as MasterData : null;
    }

    /* ===================== API CALLS ===================== */
    private fetchFromApi(): Observable<MasterData> {
        return forkJoin({
            frameManufacturers: this.get(this.endpoints.frameManufacturers),
            // frames: this.get(this.endpoints.frames),
            glasses: this.get(this.endpoints.glasses),
            // manufacturers: this.get(this.endpoints.manufacturers),
            spacers: this.get(this.endpoints.spacers),
        });
    }

    private get(url: string): Observable<any[]> {
        return defer(() =>
            CapacitorHttp.get({
                url: `${this.baseUrl}${url}`,
                headers: this.getHeaders(),
            })
        ).pipe(
            map(this.handleSuccessResponse),
            retryWhen(this.retryWithBackoff()),
            catchError(err => throwError(() => err))
        );
    }

    /* ===================== HELPERS ===================== */

    private getHeaders(): Record<string, string> {
        return {
            Accept: 'application/json',
        };
    }

    private handleSuccessResponse(response: HttpResponse): any[] {
        if (response.status < 200 || response.status >= 300) {
            throw response;
        }
        return response.data;
    }

    /* ===================== RETRY LOGIC ===================== */

    private retryWithBackoff() {
        return (errors: Observable<any>) =>
            errors.pipe(
                mergeMap((error, retryIndex) => {
                    const attempt = retryIndex + 1;

                    if (
                        attempt > this.RETRY_COUNT ||
                        !this.isRetryableError(error?.status)
                    ) {
                        return throwError(() => error);
                    }

                    const delayTime = Math.pow(2, attempt) * this.BASE_DELAY_MS;
                    return timer(delayTime);
                })
            );
    }

    private isRetryableError(status?: number): boolean {
        return [0, 408, 429, 500, 502, 503, 504].includes(status ?? 0);
    }

    /* ===================== STORAGE FALLBACK ===================== */
    private loadFromStorage(): Observable<MasterData> {
        return defer(async () => {
            const cached = await this.storageService.get(Constants.STORAGE_KEY);
            if (!cached) {
                throw new Error('Master data not available');
            }
            return JSON.parse(cached) as MasterData;
        });
    }
}
